/* global Office, Excel */
import "./taskpane.css";
import { supabase } from "./supabaseClient";

// State
let appState = {
    isAuthenticated: false,
    context: {
        project: '',
        meeting: ''
    },
    settings: {
        language: 'pt',
        provider: 'openai',
        model: 'gpt-4o',
        apiKey: '',
        prompt: 'You are a meeting minutes assistant. Format the following text. You MUST return a JSON object with the following keys: "description", "action", "responsible", "deadline", "status".',
        layout: {
            cellDate: '',
            cellNumber: '',
            colActions: '',
            colResponsible: '',
            colDeadline: '',
            colStatus: ''
        }
    }
};

const MODELS = {
    openai: [
        { value: 'gpt-4o', text: 'GPT-4o' },
        { value: 'gpt-4-turbo', text: 'GPT-4 Turbo' },
        { value: 'gpt-3.5-turbo', text: 'GPT-3.5 Turbo' }
    ],
    gemini: [
        { value: 'gemini-2.0-flash', text: 'Gemini 2.0 Flash' },
        { value: 'gemini-2.0-pro', text: 'Gemini 2.0 Pro' },
        { value: 'gemini-2.5', text: 'Gemini 2.5' },
        { value: 'gemini-3.0', text: 'Gemini 3.0' }
    ]
};

Office.onReady(async (info) => {
    if (info.host === Office.HostType.Excel) {
        loadSettings();
        setupEventListeners();
        await checkAuthState();
    }
});

async function checkAuthState() {
    const { data: { session } } = await supabase.auth.getSession();
    if (session) {
        appState.isAuthenticated = true;
        appState.userEmail = session.user.email;
    } else {
        appState.isAuthenticated = false;
        appState.userEmail = null;
    }
    updateView();

    // Listen for auth state changes
    supabase.auth.onAuthStateChange((event, session) => {
        if (session) {
            appState.isAuthenticated = true;
            appState.userEmail = session.user.email;
        } else {
            appState.isAuthenticated = false;
            appState.userEmail = null;
        }
        updateView();
    });
}

function setupEventListeners() {
    // Login & Signup
    document.getElementById("btn-login").onclick = handleLogin;
    document.getElementById("btn-signup").onclick = handleSignUp;

    // Settings
    document.getElementById("btn-open-settings").onclick = () => switchView('settings');
    document.getElementById("btn-back-settings").onclick = () => switchView('main');
    document.getElementById("btn-save-settings").onclick = saveSettings;
    document.getElementById("btn-logout").onclick = handleLogout;

    // Dynamic Model Dropdown
    document.getElementById("setting-provider").onchange = updateModelOptions;

    // Cell Picker buttons
    document.querySelectorAll('.btn-pick-cell').forEach(btn => {
        btn.onclick = () => pickCellFromExcel(btn.dataset.target, btn);
    });

    // Context inputs - save on change
    document.getElementById("context-project").onchange = saveContext;
    document.getElementById("context-meeting").onchange = saveContext;

    // Main Run
    document.getElementById("run").onclick = run;
}

async function handleLogin() {
    const email = document.getElementById("login-email").value;
    const pass = document.getElementById("login-password").value;
    const messageEl = document.getElementById("login-message");

    if (!email || !pass) {
        messageEl.innerText = "Please enter email and password.";
        return;
    }

    messageEl.innerText = "Logging in...";

    const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: pass
    });

    if (error) {
        messageEl.innerText = error.message;
    } else {
        appState.isAuthenticated = true;
        appState.userEmail = data.user.email;
        saveState();
        updateView();
    }
}

async function handleLogout() {
    await supabase.auth.signOut();
    appState.isAuthenticated = false;
    appState.userEmail = null;
    saveState();
    updateView();
}

async function handleSignUp() {
    const email = document.getElementById("login-email").value;
    const pass = document.getElementById("login-password").value;
    const messageEl = document.getElementById("login-message");

    if (!email || !pass) {
        messageEl.innerText = "Please enter email and password.";
        return;
    }

    if (pass.length < 6) {
        messageEl.innerText = "Password must be at least 6 characters.";
        return;
    }

    messageEl.innerText = "Creating account...";

    const { data, error } = await supabase.auth.signUp({
        email: email,
        password: pass
    });

    if (error) {
        messageEl.innerText = error.message;
    } else {
        messageEl.style.color = "green";
        messageEl.innerText = "Account created! Check your email to confirm, then log in.";
    }
}

function loadSettings() {
    const saved = localStorage.getItem('aiMinutesState');
    if (saved) {
        appState = JSON.parse(saved);
        // Ensure layout object exists in older saves
        if (!appState.settings.layout) {
            appState.settings.layout = { cellDate: '', cellNumber: '', colActions: '', colResponsible: '', colDeadline: '', colStatus: '' };
        }
        if (!appState.context) {
            appState.context = { project: '', meeting: '' };
        }
        if (!appState.settings.language) {
            appState.settings.language = 'pt';
        }
    }

    // Populate settings inputs
    document.getElementById("setting-language").value = appState.settings.language || 'pt';
    document.getElementById("setting-provider").value = appState.settings.provider;
    updateModelOptions(); // Populate models based on provider
    document.getElementById("setting-model").value = appState.settings.model;
    document.getElementById("setting-apikey").value = appState.settings.apiKey;
    document.getElementById("setting-prompt").value = appState.settings.prompt;

    // Layout inputs
    document.getElementById("setting-cell-date").value = appState.settings.layout.cellDate || '';
    document.getElementById("setting-cell-number").value = appState.settings.layout.cellNumber || '';
    document.getElementById("setting-col-actions").value = appState.settings.layout.colActions || '';
    document.getElementById("setting-col-responsible").value = appState.settings.layout.colResponsible || '';
    document.getElementById("setting-col-deadline").value = appState.settings.layout.colDeadline || '';
    document.getElementById("setting-col-status").value = appState.settings.layout.colStatus || '';

    // Context inputs
    document.getElementById("context-project").value = appState.context.project || '';
    document.getElementById("context-meeting").value = appState.context.meeting || '';
}

function updateModelOptions() {
    const provider = document.getElementById("setting-provider").value;
    const modelSelect = document.getElementById("setting-model");
    const models = MODELS[provider] || [];

    modelSelect.innerHTML = '';
    models.forEach(m => {
        const option = document.createElement("option");
        option.value = m.value;
        option.innerText = m.text;
        modelSelect.appendChild(option);
    });
}

function saveSettings() {
    appState.settings.language = document.getElementById("setting-language").value;
    appState.settings.provider = document.getElementById("setting-provider").value;
    appState.settings.model = document.getElementById("setting-model").value;
    appState.settings.apiKey = document.getElementById("setting-apikey").value;
    appState.settings.prompt = document.getElementById("setting-prompt").value;

    appState.settings.layout = {
        cellDate: document.getElementById("setting-cell-date").value,
        cellNumber: document.getElementById("setting-cell-number").value,
        colActions: document.getElementById("setting-col-actions").value,
        colResponsible: document.getElementById("setting-col-responsible").value,
        colDeadline: document.getElementById("setting-col-deadline").value,
        colStatus: document.getElementById("setting-col-status").value
    };

    saveState();
    switchView('main');
}

function saveContext() {
    appState.context.project = document.getElementById("context-project").value;
    appState.context.meeting = document.getElementById("context-meeting").value;
    saveState();
}

async function pickCellFromExcel(targetInputId, button) {
    const targetInput = document.getElementById(targetInputId);
    const isColumnOnly = targetInputId.includes('col-');

    button.classList.add('picking');
    button.innerText = '...';

    try {
        await Excel.run(async (context) => {
            const range = context.workbook.getSelectedRange();
            range.load("address");
            await context.sync();

            // Address format: "Sheet1!A1" or "A1:B2"
            let address = range.address;
            // Remove sheet name if present
            if (address.includes('!')) {
                address = address.split('!')[1];
            }
            // Get just the first cell if a range
            if (address.includes(':')) {
                address = address.split(':')[0];
            }

            if (isColumnOnly) {
                // Extract just the column letter(s)
                const colMatch = address.match(/^([A-Z]+)/i);
                if (colMatch) {
                    targetInput.value = colMatch[1].toUpperCase();
                }
            } else {
                targetInput.value = address;
            }
        });
    } catch (error) {
        console.error('Cell picker error:', error);
    } finally {
        button.classList.remove('picking');
        button.innerText = '📍';
    }
}

function saveState() {
    localStorage.setItem('aiMinutesState', JSON.stringify(appState));
}

function switchView(viewName) {
    document.querySelectorAll('.view-container').forEach(el => el.classList.add('hidden'));

    if (viewName === 'login') {
        document.getElementById('view-login').classList.remove('hidden');
    } else if (viewName === 'settings') {
        document.getElementById('view-settings').classList.remove('hidden');
    } else {
        document.getElementById('view-main').classList.remove('hidden');
    }
}

function updateView() {
    if (!appState.isAuthenticated) {
        switchView('login');
    } else {
        switchView('main');
    }
}

export async function run() {
    try {
        const apiKey = appState.settings.apiKey;
        let prompt = appState.settings.prompt;
        const layout = appState.settings.layout;

        if (!apiKey) {
            document.getElementById("message-area").innerText = "Please configure your API Key in Settings.";
            return;
        }

        await Excel.run(async (context) => {
            const sheet = context.workbook.worksheets.getActiveWorksheet();
            const range = context.workbook.getSelectedRange();
            range.load("values, rowIndex");

            // Read Context Cells if configured
            let contextInfo = "";
            if (layout.cellDate) {
                const dateRange = sheet.getRange(layout.cellDate);
                dateRange.load("values");
                await context.sync(); // Sync first to get Date
                contextInfo += `Meeting Date: ${dateRange.values[0][0]}. `;
            }
            if (layout.cellNumber) {
                const numRange = sheet.getRange(layout.cellNumber);
                numRange.load("values");
                await context.sync(); // Sync again if needed (or batch above)
                contextInfo += `Meeting Number: ${numRange.values[0][0]}. `;
            }

            // Re-sync range values if not loaded in previous syncs (though context.sync syncs all queued)
            // range values should be available if we queue them before the first sync.
            // Let's just make sure we act on the selected range now.
            // We need to re-load if we did intermediate syncs that might have cleared the object? No, but let's be safe.
            // Actually, best practice: Queue all loads, then one sync. But here we depend on conditional cells.
            // So we might need multiple syncs.

            if (!contextInfo && (layout.cellDate || layout.cellNumber)) {
                // If we had conditional logic, we did syncs.
                // Ensure range is loaded.
                range.load("values, rowIndex");
                await context.sync();
            } else if (!layout.cellDate && !layout.cellNumber) {
                // If no context cells, we still need one sync for the main range
                await context.sync();
            }

            const originalText = range.values[0][0];

            if (!originalText) {
                document.getElementById("message-area").innerText = "Please select a cell with text.";
                return;
            }

            document.getElementById("message-area").innerText = "Processing with AI...";

            // enhance prompt with JSON requirement and context
            const jsonInstruction = `
        Context: ${contextInfo}
        RETURN JSON ONLY. Structure: {"description": "...", "action": "...", "responsible": "...", "deadline": "...", "status": "..."}
        If a field is not present, use empty string. Status options: "Em aberto", "Fechado", "On Hold", "INFO".
        `;

            const fullPrompt = `${prompt}\n${jsonInstruction}`;

            const processedJsonStr = await processWithIA(originalText, apiKey, fullPrompt);

            // Parse JSON
            let data;
            try {
                // Clean markdown code blocks if present
                const cleanJson = processedJsonStr.replace(/```json/g, '').replace(/```/g, '').trim();
                data = JSON.parse(cleanJson);
            } catch (e) {
                console.error("JSON Parse Error", e);
                document.getElementById("message-area").innerText = "Error parsing AI response. Check console.";
                return;
            }

            // Write to Excel
            // Description goes to selected cell
            range.values = [[data.description]];

            const currentRow = range.rowIndex + 1; // 1-based index for address

            if (layout.colActions) {
                sheet.getRange(`${layout.colActions}${currentRow}`).values = [[data.action]];
            }
            if (layout.colResponsible) {
                sheet.getRange(`${layout.colResponsible}${currentRow}`).values = [[data.responsible]];
            }
            if (layout.colDeadline) {
                sheet.getRange(`${layout.colDeadline}${currentRow}`).values = [[data.deadline]];
            }
            if (layout.colStatus) {
                sheet.getRange(`${layout.colStatus}${currentRow}`).values = [[data.status]];
            }

            range.format.autofitColumns(); // Autofit active column
            // Optionally autofit others? Maybe too expensive. Let's just autofit the main one.

            await context.sync();

            document.getElementById("message-area").innerText = "Done!";
        });
    } catch (error) {
        console.error(error);
        document.getElementById("message-area").innerText = "Error: " + error.message;
    }
}

async function processWithIA(text, apiKey, prompt) {
    const provider = appState.settings.provider;
    const model = appState.settings.model;

    if (provider === 'openai') {
        return await callOpenAI(text, apiKey, model, prompt);
    } else if (provider === 'gemini') {
        return await callGemini(text, apiKey, model, prompt);
    } else {
        throw new Error("Unknown provider: " + provider);
    }
}

async function callOpenAI(text, apiKey, model, prompt) {
    const url = "https://api.openai.com/v1/chat/completions";
    const body = {
        model: model,
        messages: [
            { role: "system", content: prompt },
            { role: "user", content: text }
        ],
        temperature: 0.7
    };

    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${apiKey}`
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || "OpenAI API Error");
    }

    const data = await response.json();
    return data.choices[0].message.content;
}

async function callGemini(text, apiKey, model, prompt) {
    // Gemini API format: https://generativelanguage.googleapis.com/v1beta/models/[MODEL]:generateContent?key=[KEY]
    // clean model name if needed (handle both 'gemini-2.0-flash' and simple names if api differs)
    // For now passing exact value from dropdown.

    // Note: Gemini 1.5/2.0+ endpoints might vary, using standard v1beta/models endpoint
    const url = `https://generativelanguage.googleapis.com/v1beta/models/${model}:generateContent?key=${apiKey}`;

    const body = {
        contents: [{
            parts: [{
                text: `${prompt}\n\nInput Text:\n${text}`
            }]
        }]
    };

    const response = await fetch(url, {
        method: "POST",
        headers: {
            "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
    });

    if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || "Gemini API Error");
    }

    const data = await response.json();
    // Verify response structure
    if (data.candidates && data.candidates[0] && data.candidates[0].content) {
        return data.candidates[0].content.parts[0].text;
    }
    return "Error: Unexpected response format from Gemini";
}
